import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Info, Search, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useEntitlements } from '@/stores/entitlements';
import { CHANGELOG, FEATURES, PLATFORM_UPDATED, PLATFORM_VERSION } from '@/lib/platformInfo';

/** 'YYYY-MM-DD' em pt-BR, sem depender de utils. */
function fmtDate(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('pt-BR');
}

/** Conteúdo vive em `@/lib/platformInfo`; editar lá a cada ajuste. */
export function PlatformInfoPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const [busca, setBusca] = useState('');

  const areas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return FEATURES;
    return FEATURES.map((a) => ({
      ...a,
      items: a.items.filter((i) =>
        `${i.nome} ${i.onde ?? ''} ${i.detalhe ?? ''} ${(i.papeis ?? []).join(' ')}`.toLowerCase().includes(q),
      ),
    })).filter((a) => a.items.length > 0);
  }, [busca]);

  const total = FEATURES.reduce((n, a) => n + a.items.length, 0);

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Info / Updates</h1>
          <p className="text-sm text-muted-foreground">
            Todas as ferramentas e funcionalidades da plataforma, com o caminho de cada uma, e os últimos
            updates. Versão {PLATFORM_VERSION} · atualizado em {fmtDate(PLATFORM_UPDATED)} · {total} itens.
          </p>
        </div>
        <Link
          to="/manuais"
          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-input px-4 text-sm font-medium hover:bg-accent"
        >
          <BookOpen className="h-4 w-4" />
          Manuais
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar funcionalidade, tela ou caminho"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <Info className="h-4 w-4 text-primary" /> O QUE A PLATAFORMA TEM
        </h2>
        {areas.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Nada encontrado para essa busca.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {areas.map((f) => (
              <Card key={f.area}>
                <CardContent className="space-y-2 p-4">
                  <p className="text-sm font-bold">{f.area}</p>
                  <ul className="space-y-1.5">
                    {f.items.map((it) => (
                      <li key={it.nome} className="text-xs">
                        <span className="font-semibold">{it.nome}</span>
                        {it.onde && (
                          <code className="ml-1.5 rounded bg-secondary px-1 py-0.5 text-[10px] text-muted-foreground">
                            {it.onde}
                          </code>
                        )}
                        {it.papeis && (
                          <span
                            className="ml-1.5 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"
                            title={`Acesso: ${it.papeis.join(', ')}`}
                          >
                            {it.papeis.includes(papel ?? '') ? it.papeis.join(', ') : `restrito: ${it.papeis.join(', ')}`}
                          </span>
                        )}
                        {it.detalhe && <span className="text-muted-foreground"> — {it.detalhe}</span>}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <Sparkles className="h-4 w-4 text-primary" /> ÚLTIMOS UPDATES
        </h2>
        <div className="space-y-3">
          {CHANGELOG.map((c) => (
            <Card key={`${c.date}-${c.title}`}>
              <CardContent className="space-y-1.5 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold">{c.title}</p>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    {fmtDate(c.date)}
                  </span>
                </div>
                <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                  {c.items.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
